import { Badge, Button, Spinner } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Eye } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DateCell } from '@/components/data-table/cells';
import { DataTable } from '@/components/data-table/data-table';
import { usePresignedDownload } from '@/hooks/use-presigned-download';
import type { ReportFile } from '@/services/api/reports';
import { formatBytes } from '@/utils/format';
import { extractFileName } from '@/utils/string';

const PAGE_SIZE = 25;

// fileName is derived onto the row before columns are built — the legacy table
// sorted the File Name column on a key that did not exist on the row (deliberate fix).
interface ReportRow extends ReportFile {
  fileName: string;
}

const isCsvKey = (key: string) => key.toLowerCase().endsWith('.csv');

/**
 * Key of the most recently modified file — the row wearing the "Latest" badge.
 * Anchored to lastModified (ISO strings compare lexicographically), not the current
 * sort order, so re-sorting the table never moves the badge. Ties keep the first file.
 */
export const findLatestFileKey = (files: ReportFile[]): string | null => {
  let latest: ReportFile | null = null;
  for (const file of files) {
    if (!latest || file.lastModified > latest.lastModified) latest = file;
  }
  return latest?.key ?? null;
};

const DownloadCell = ({ fileKey, isDownloading, onDownload }: { fileKey: string; isDownloading: boolean; onDownload: (key: string) => void }) => (
  <Button aria-label={`Download ${extractFileName(fileKey)}`} disabled={isDownloading} onClick={() => onDownload(fileKey)} size="icon" type="button" variant="ghost">
    {isDownloading ? <Spinner className="size-4" /> : <Download className="size-4" />}
  </Button>
);
DownloadCell.displayName = 'DownloadCell';

// Only CSV files get the in-app viewer; other report formats stay download-only.
const ViewCell = ({ fileKey, viewPathname }: { fileKey: string; viewPathname: string }) => (
  <Button asChild aria-label={`View ${extractFileName(fileKey)}`} size="icon" type="button" variant="ghost">
    <Link to={{ pathname: viewPathname, search: `?key=${encodeURIComponent(fileKey)}` }}>
      <Eye className="size-4" />
    </Link>
  </Button>
);
ViewCell.displayName = 'ViewCell';

export interface ReportFilesTableProps {
  files: ReportFile[] | undefined;
  isPending: boolean;
  /** Route of the CSV viewer page this table links to (the file key goes in `?key=`). */
  viewPathname: string;
  emptyMessage: string;
  /** Adds a Kind column, for prefixes that hold more than one kind of file */
  kindOf?: (file: ReportFile) => string;
  /** Extra per-row action rendered before view and download; return null for rows without one */
  renderAction?: (file: ReportFile) => ReactNode;
}

/** S3 report file listing with view + presigned-URL download actions. */
export const ReportFilesTable = ({ files, isPending, viewPathname, emptyMessage, kindOf, renderAction }: ReportFilesTableProps) => {
  const { download: handleDownload, downloadingKeys } = usePresignedDownload();

  const rows = useMemo((): ReportRow[] => (files ?? []).map((file) => ({ ...file, fileName: extractFileName(file.key) })), [files]);
  const latestKey = useMemo(() => findLatestFileKey(files ?? []), [files]);

  const columns = useMemo(
    (): ColumnDef<ReportRow, unknown>[] => [
      {
        accessorKey: 'fileName',
        header: 'File Name',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs">{row.original.fileName}</span>
            {row.original.key === latestKey ? <Badge className="text-[10px] uppercase">Latest</Badge> : null}
          </span>
        ),
      },
      ...(kindOf
        ? [
            {
              id: 'kind',
              header: 'Kind',
              accessorFn: (row: ReportRow) => kindOf(row),
              cell: ({ row }: { row: { original: ReportRow } }) => <span className="capitalize">{kindOf(row.original)}</span>,
            } satisfies ColumnDef<ReportRow, unknown>,
          ]
        : []),
      {
        accessorKey: 'size',
        header: 'Size',
        cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{formatBytes(row.original.size)}</span>,
      },
      {
        accessorKey: 'lastModified',
        header: 'Last Modified',
        cell: ({ row }) => <DateCell value={row.original.lastModified} />,
      },
      ...(renderAction
        ? [
            {
              id: 'action',
              enableSorting: false,
              cell: ({ row }: { row: { original: ReportRow } }) => renderAction(row.original),
            } satisfies ColumnDef<ReportRow, unknown>,
          ]
        : []),
      {
        id: 'view',
        enableSorting: false,
        cell: ({ row }) => (isCsvKey(row.original.key) ? <ViewCell fileKey={row.original.key} viewPathname={viewPathname} /> : null),
      },
      {
        id: 'download',
        enableSorting: false,
        cell: ({ row }) => <DownloadCell fileKey={row.original.key} isDownloading={downloadingKeys.has(row.original.key)} onDownload={handleDownload} />,
      },
    ],
    [downloadingKeys, handleDownload, kindOf, latestKey, renderAction, viewPathname],
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      defaultSorting={[{ id: 'lastModified', desc: true }]}
      emptyMessage={emptyMessage}
      isLoading={isPending}
      pageSize={PAGE_SIZE}
      surface={false}
    />
  );
};
ReportFilesTable.displayName = 'ReportFilesTable';
